import React from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from "@tanstack/react-table"
import {
    chakra,
    FocusLock,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Popover,
    PopoverArrow,
    PopoverContent,
    PopoverTrigger,
    Table,
    TableCaption,
    TableContainer,
    Tbody,
    Td,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react"
import {
    CheckIcon,
    ChevronDownIcon,
    ChevronUpDownIcon,
    ChevronUpIcon,
    FunnelIcon as SolidFunnelIcon
} from "@heroicons/react/20/solid"
import {FunnelIcon} from "@heroicons/react/24/outline"

import {FilterByType, SortByType} from "@backend/types"
import {EmptyState} from "@components/app"

type ReactTableProps = {
    caption?: string | React.ReactNode,
    columns: ColumnDef<any>[],
    isLoading?: boolean,
    sort?: [] | {
        id: string,
        desc: boolean
    }[],
    emptyState?: {
        title?: string,
        description?: string
    }
    onFilterChange?: ({sortBy, filterBy}: { sortBy?: SortByType[], filterBy?: FilterByType[] }) => void,
    data: any[],
    className?: string
}

export function ReactTable({caption, columns, onFilterChange, data, emptyState, className}: ReactTableProps) {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [rowSelection, setRowSelection] = React.useState({})
    const {getHeaderGroups, getRowModel} = useReactTable({
        columns,
        data,
        state: {
            columnFilters,
            sorting,
            rowSelection
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onColumnFiltersChange: (updaterFn) => {
            // @ts-ignore
            const updatedFilter = updaterFn(columnFilters)
            onFilterChange({filterBy: updatedFilter})
            setColumnFilters(updatedFilter)
        },
        onSortingChange: (updaterFn) => {
            // @ts-ignore
            const updatedSort = updaterFn(sorting)
            onFilterChange({sortBy: updatedSort})
            setSorting(updatedSort)
        },
        manualSorting: true,
        enableMultiSort: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel()
    })

    if (!data.length && !columnFilters.length)
        return <EmptyState title={emptyState?.title} description={emptyState?.description}/>

    return <TableContainer>
        <Table className={className}>
            {caption && <TableCaption>{caption}</TableCaption>}
            {(columnFilters.length > 0 && data.length === 0) && <TableCaption>No Data</TableCaption>}
            <Thead>
                {getHeaderGroups().map((headerGroup) => (
                    <Tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                            const filteredHeader: string[] = header.column.getFilterValue() as string[] || []
                            const isFiltered: boolean = header.column.getIsFiltered()


                            return <Th
                                key={header.id}
                                fontWeight="medium"
                                bg="gray.50"
                                borderBottom="1px"
                                borderColor="gray.200"
                                color="gray.500"
                                px={6}
                                py={3}
                                className="text-left text-xs tracking-wider uppercase"
                            >
                                {header.isPlaceholder ? null :
                                    <div className="flex items-center">
                                        <div
                                            {...{
                                                className: header.column.getCanSort()
                                                    ? "flex items-center cursor-pointer select-none"
                                                    : "flex items-center",
                                                onClick: header.column.getToggleSortingHandler(),
                                            }}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                            {header.column.getCanSort() &&
                                              <chakra.span pl="2">
                                                  {{
                                                          asc: <ChevronUpIcon className="text-blue-500 w-5 h-5"
                                                                              aria-label="sorted ascending"/>,
                                                          desc: <ChevronDownIcon className="text-blue-500 w-5 h-5"
                                                                                 aria-label="sorted descending"/>,
                                                      }[header.column.getIsSorted() as string] ??
                                                    <ChevronUpDownIcon className="w-5 h-5"/>}
                                              </chakra.span>}
                                        </div>

                                        {header.column.getCanFilter() ? (
                                            typeof header.column.columnDef.meta === "function" ?
                                                isFiltered ? <IconButton aria-label="clear"
                                                                         variant="ghost"
                                                                         colorScheme="red"
                                                                         onClick={() => {
                                                                             header.column.setFilterValue(() => {
                                                                             })
                                                                         }}
                                                                         icon={<SolidFunnelIcon
                                                                             className="w-4 h-4"/>}></IconButton> :
                                                    <Popover
                                                        placement="bottom"
                                                    >
                                                        {({isOpen, onClose}) => (
                                                            <>
                                                                <PopoverTrigger>
                                                                    <IconButton aria-label="Search"
                                                                                variant="ghost"
                                                                                icon={<FunnelIcon
                                                                                    className="w-4 h-4"/>}/>
                                                                </PopoverTrigger>
                                                                <PopoverContent p={5}>
                                                                    <FocusLock>
                                                                        <PopoverArrow/>

                                                                        {header.column.columnDef.meta(val => {
                                                                            header.column.setFilterValue(() => [val.value])
                                                                            onClose()
                                                                        })}
                                                                    </FocusLock>

                                                                </PopoverContent>
                                                            </>
                                                        )}
                                                    </Popover>
                                                :
                                                <Menu closeOnSelect={false}>
                                                    <MenuButton
                                                        as={IconButton}
                                                        rounded="full"
                                                        colorScheme={isFiltered ? "green" : null}
                                                        variant="ghost"
                                                        icon={isFiltered ? <SolidFunnelIcon className="w-4 h-4"/> :
                                                            <FunnelIcon className="w-4 h-4"/>}
                                                    />
                                                    <MenuList>
                                                        <MenuItem onClick={() => {
                                                            header.column.setFilterValue(() => {
                                                            })
                                                        }}>
                                                            {isFiltered ? <span className="w-4 mr-1"></span> :
                                                                <CheckIcon className="w-4 h-4 mr-1 text-green-900"/>}
                                                            All
                                                        </MenuItem>
                                                        <MenuDivider/>
                                                        {(Object.keys(header.column.columnDef.meta || {})).map((metaKey) =>
                                                            <MenuItem
                                                                key={metaKey}
                                                                onClick={() => {
                                                                    header.column.setFilterValue(() => [metaKey])
                                                                }}
                                                            >
                                                                {(isFiltered && filteredHeader.includes(metaKey)) ?
                                                                    <CheckIcon
                                                                        className="w-4 h-4 mr-1 text-green-900"/> :
                                                                    <span className="w-4 mr-1"></span>
                                                                }
                                                                {header.column.columnDef.meta[metaKey]}
                                                            </MenuItem>)}
                                                    </MenuList>
                                                </Menu>
                                        ) : null}
                                    </div>

                                }
                            </Th>
                        })}
                    </Tr>
                ))}
            </Thead>
            <Tbody
                bg="white"
                className="divide-y divide-gray-100">
                {getRowModel().rows.map((row) => (
                    <Tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <Td key={cell.id}
                                className="text-sm font-medium text-gray-900 whitespace-break-spaces">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Td>
                        ))}
                    </Tr>
                ))}
            </Tbody>
        </Table>
    </TableContainer>
}